// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// Enrollment review notes — a comment thread on the enrollment review panel (mig 436).
// Owner, 2026-10-01: a note used to overwrite the clinical (intake) notes and the note before it.
// Now each note is appended, listed newest first, and paged on the server.

import { useRef, useState } from 'react'
import { Pager } from '../../components/Pager'
import { formatUsDateTime } from '../../api/format'
import { friendlyError } from '../../api/errors'
import { useAddEnrollmentReviewNote, useEnrollmentReviewNotes } from '../../hooks/queries/enrollmentReviewNotes'

const PAGE_SIZE = 10
const MAX_LENGTH = 2000   // CareRxEnrollmentReviewNotesFunctions.MaxNoteLength

export default function EnrollmentReviewNotes({ patientId, editHint = true, onAdded }: {
  patientId: string
  /** The enrollment review panel points at its Edit enrollment button; other hosts have none. */
  editHint?: boolean
  /** The host refreshes what shows the latest note (the queue's Last review column). */
  onAdded?: () => void
}) {
  const [draft, setDraft] = useState('')
  const [page, setPage] = useState(1)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const notes = useEnrollmentReviewNotes(patientId, page, PAGE_SIZE)
  // The page at the moment the note lands, not when Add was pressed (the user may page meanwhile).
  const pageRef = useRef(page); pageRef.current = page
  // After an add, page 1 is fetched once: by the page change, or directly when already on page 1.
  const add = useAddEnrollmentReviewNote(patientId, () => {
    setDraft('')
    if (pageRef.current === 1) void notes.refetch()
    else setPage(1)
    onAdded?.()
  })
  const text = draft.trim()

  const submit = () => {
    setMsg(null)
    add.mutate(text, {
      onSuccess: () => setMsg({ ok: true, text: 'Review note added.' }),
      onError: e => setMsg({ ok: false, text: friendlyError(e) }),
    })
  }

  const total = notes.data?.total ?? 0
  return (
    <div style={{ marginTop: 4 }}>
      <textarea placeholder="Add a review note" value={draft} maxLength={MAX_LENGTH} onChange={e => setDraft(e.target.value)}
        style={{ width: '100%', minHeight: 56, padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', background: 'var(--color-surface)', color: 'var(--color-text)', fontFamily: 'inherit' }} />
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button disabled={add.isPending || !text} onClick={submit}
          style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: text ? 'pointer' : 'default', opacity: text ? 1 : 0.55 }}>
          Add review note
        </button>
        {editHint && <span style={{ fontSize: 11.5, color: 'var(--color-text-mute)' }}>Read-only — use <b>Edit enrollment</b> above to change details.</span>}
      </div>
      {msg && <div style={{ fontSize: 12, marginTop: 8, color: msg.ok ? 'var(--color-primary)' : '#dc2626' }}>{msg.text}</div>}

      <section aria-label="Review notes" style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-mute)', marginBottom: 4 }}>
          Review notes{total ? ` (${total})` : ''}
        </div>
        {notes.isError && <div style={{ fontSize: 12, color: '#dc2626' }}>{friendlyError(notes.error)}</div>}
        {notes.isSuccess && total === 0 && page === 1 && (
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>No review notes yet.</div>
        )}
        {(notes.data?.items ?? []).map(n => (
          <div key={n.id} style={{ fontSize: 12.5, padding: '7px 0', borderTop: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)' }}>
              <b style={{ color: 'var(--color-text-soft)' }}>{n.author_name || 'Reviewer'}</b> · {formatUsDateTime(n.created_at)}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{n.review_note}</div>
          </div>
        ))}
        <Pager page={page} totalPages={notes.data?.total_pages ?? 1} total={total} pageSize={PAGE_SIZE}
          onPage={setPage} showPageSize={false} label="notes" />
      </section>
    </div>
  )
}
